import PropTypes from 'prop-types'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'

// Component imports
import { Footer, FooterContent } from './footer'
import {
  AppHeader,
  HeaderBtn,
  HeaderItem,
  Logo,
  LogoImg,
  LogoNav,
  LogoSmall,
  Navbar,
  NavBarBrandBox,
} from './header'
import LanguagesDropdown from './languagesDropdown'
import ProfileMenu from './profileMenu'
import SettingsDropdown from './settingDropdown'
import Sidebar from './sidebar'
import { VerticalMenu } from './sidebar-styles'
import Main from './styles'
import UserCompanyDropdown from './userCompanyDropdown'

// Context, hooks and utilities
import { MenuItemsCounterContext } from '../../contexts/menuItemsCounterContext'
import useAgencyType from '../../hooks/useAgencyType'
import cookiesToObject from '../../utils/cookiesHandler'

// Constants and assets
import FullLogo from '../../assets/logos/full-logo.png'
import AIIcon from '../../assets/images/AI.png'
import { useTheme } from '../../contexts/themeContext'
import permissions from '../../features/configuration/utils/rolesPermissions'
import { getImageColorFilters } from '../../utils/colorUtilities'
import { MiniLogo } from '../styled'

// Menu configuration imports
import { userContext } from '../../contexts/userContext'
import NotificationsCenter from '../../features/notifications/components/notification/NotificationsCenter'
import { createMenuStructure } from './utils/menuConfig'
import ViewportWarning from './viewPortWarning'

/**
 * Main Layout component that provides the application structure
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render in the main content area
 */
const Layout = ({ children }) => {
  // Hooks and context
  const { t, i18n } = useTranslation()
  const { theme } = useTheme()

  const { isAgency, agencyId } = cookiesToObject(document.cookie)
  const { organizationType } = useAgencyType(agencyId, true, true)
  const { menuItemsCounter } = useContext(MenuItemsCounterContext)
  const { state: user } = useContext(userContext)

  // State
  const [collapsed, setCollapsed] = useState(() => window.innerWidth < 992)
  const [showChildren, setShowChildren] = useState(
    () => window.innerWidth > 992,
  )

  // Toggle sidebar collapsed state
  const toggleSidebar = useCallback(() => {
    setCollapsed((prev) => {
      const newCollapsedState = !prev

      if (!newCollapsedState && !showChildren) {
        setShowChildren(true)
      }

      return newCollapsedState
    })
  }, [showChildren])

  // Handle responsive layout changes
  useEffect(() => {
    let resizeTimer

    const handleResize = () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        const isSmallScreen = window.innerWidth < 992
        setShowChildren(!isSmallScreen)
        setCollapsed(isSmallScreen)
      }, 100)
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(resizeTimer)
    }
  }, [])

  // const menuItems = useMemo(
  //   () =>
  //     createMenuStructure(
  //       t,
  //       isAgency,
  //       organizationType,
  //       menuItemsCounter,
  //       theme,
  //       permissions,
  //     ),
  //   [t, isAgency, organizationType, menuItemsCounter, user?.companyId],
  // )
  const menuItems = createMenuStructure(
    t,
    isAgency,
    organizationType,
    menuItemsCounter,
    theme,
    permissions,
  )

  return (
    <div>
      {showChildren ? (
        <>
          <NavBarSection
            collapsed={collapsed}
            language={i18n?.language}
            miniLogo={MiniLogo}
            fullLogo={FullLogo}
            theme={theme}
          />

          <HeaderSection
            collapsed={collapsed}
            language={i18n?.language}
            toggleSidebar={toggleSidebar}
          />

          <VerticalMenu collapsed={collapsed}>
            <Sidebar isSidebarExpanded={!collapsed} menuItems={menuItems} />
          </VerticalMenu>

          <Main collapsed={collapsed} locale={i18n?.language}>
            {children}
          </Main>

          <FooterSection
            collapsed={collapsed}
            t={t}
            language={i18n?.language}
          />
        </>
      ) : (
        <ViewportWarning />
      )}
    </div>
  )
}

/**
 * Navigation bar brand section component
 */
const NavBarSection = ({ collapsed, language, miniLogo, fullLogo, theme }) => (
  <NavBarBrandBox collapsed={collapsed}>
    <LogoNav to="/" collapsed={collapsed.toString()}>
      <Logo collapsed={collapsed} locale={language}>
        {collapsed ? (
          <MiniLogo
            primaryColor={theme.collapsedLogoPrimary}
            secondaryColor={theme.collapsedLogoSecondary}
            height={47}
            width={47}
          />
        ) : (
          <LogoImg
            height="auto"
            width="130px"
            className="text-center"
            src={fullLogo}
            alt="Full logo"
            style={{
              filter: getImageColorFilters(theme.brandColorOrange),
            }}
          />
        )}
      </Logo>
      <LogoSmall to="/">
        <img height="60px" width="auto" src={miniLogo} alt="Mini logo" />
      </LogoSmall>
    </LogoNav>
  </NavBarBrandBox>
)

/**
 * Header section component with navigation controls
 */
const HeaderSection = ({ collapsed, language, toggleSidebar }) => (
  <AppHeader collapsed={collapsed} locale={language}>
    <Navbar>
      <div className="d-flex align-items-center">
        <HeaderItem
          type="button"
          className="btn btn-sm px-3"
          id="vertical-menu-btn"
          onClick={toggleSidebar}
        >
          <HeaderBtn />
        </HeaderItem>
        <UserCompanyDropdown />
      </div>

      <div className="d-flex align-items-center gap-4">
        <SettingsDropdown />
        <LanguagesDropdown />
        {/* <NotificationDropdown /> */}
        <NotificationsCenter />
        <ProfileMenu />
      </div>
    </Navbar>
  </AppHeader>
)

/**
 * Footer section component
 */
const FooterSection = ({ collapsed, t, language }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const isAssistino = location.pathname.startsWith('/assistino')
  const { theme } = useTheme()

  const handleAssistinoClick = () => {
    navigate('/assistino')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleAssistinoClick()
    }
  }

  return (
    <>
      <Footer collapsed={collapsed} isAssistino={isAssistino}>
        {t('layout.talentinoAI')}{' '}
        <FooterContent>
          © {new Date().getFullYear()}. {t('layout.allRightsReserved')}.
        </FooterContent>
      </Footer>
    </>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
}

// PropTypes for component sections
NavBarSection.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  language: PropTypes.string,
  miniLogo: PropTypes.string.isRequired,
  fullLogo: PropTypes.string.isRequired,
}

HeaderSection.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  language: PropTypes.string,
  toggleSidebar: PropTypes.func.isRequired,
}

FooterSection.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  language: PropTypes.string,
}

export default Layout
